import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  AlertCircleIcon,
  Activity01Icon,
  ArrowLeft01Icon,
  ChartBarLineIcon,
  CheckmarkCircle02Icon,
  CloudServerIcon,
  CodeIcon,
  Copy01Icon,
  Database01Icon,
  DollarCircleIcon,
  FolderCloudIcon,
  Globe02Icon,
  Key01Icon,
  LinkSquare01Icon,
  LockIcon,
  Mail01Icon,
  Clock01Icon,
  RefreshIcon,
  RepositoryIcon,
  ServerStack01Icon,
  Settings01Icon,
  Search01Icon,
} from '@hugeicons/core-free-icons';
import { toast } from 'sonner';

import { EnvironmentBadge, EnvironmentBadges, StatusBadge } from '@/components/applications/ApplicationBadges';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { api, apiError } from '@/lib/api';

const ENVIRONMENTS = ['dev', 'stg', 'prd'];
const STAGE_RANK = { Registro: 0, Evaluación: 1, Desarrollo: 2, Piloto: 3, Producción: 4 };
const ENVIRONMENT_RANK = { dev: 2, stg: 3, prd: 4 };

export default function ApplicationAdmin() {
  const { id } = useParams();
  const { can } = useAuth();
  const [application, setApplication] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [applicationResult, resourcesResult] = await Promise.all([
        api.get(`/applications/${id}`),
        api.get(`/applications/${id}/resources`),
      ]);
      setApplication(applicationResult.data);
      setResources(Array.isArray(resourcesResult.data) ? resourcesResult.data : []);
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const provisionDatabase = async (environment) => {
    setSubmitting(`database-${environment.environment}`);
    try {
      await api.post(
        `/applications/${id}/provision`,
        { environment: environment.environment },
        { headers: { 'Idempotency-Key': `${id}-${environment.environment}-${Date.now()}` } },
      );
      toast.success(`Base de datos ${environment.environment.toUpperCase()} solicitada`, {
        description: 'La operación quedó registrada y será procesada para este ambiente.',
      });
      await load();
    } catch (requestError) {
      toast.error('No fue posible solicitar la base de datos', { description: apiError(requestError) });
    } finally {
      setSubmitting('');
    }
  };

  const requestResource = async (environment, resourceType) => {
    setSubmitting(`${resourceType}-${environment}`);
    try {
      const response = await api.post(`/applications/${id}/resources`, {
        environment,
        resource_type: resourceType,
      });
      toast.success(
        resourceType === 's3_bucket' ? 'Solicitud de bucket registrada' : 'Solicitud de correo registrada',
        {
          description:
            response.status === 202
              ? 'VIA creó una solicitud auditable; el recurso aún no se considera disponible.'
              : 'La solicitud ya estaba registrada y conserva su estado actual.',
        },
      );
      await load();
    } catch (requestError) {
      toast.error('No fue posible registrar la solicitud', { description: apiError(requestError) });
    } finally {
      setSubmitting('');
    }
  };

  if (loading) return <AdminLoadingState />;
  if (error || !application) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 py-10">
        <Button variant="ghost" className="w-fit" asChild>
          <Link to="/aplicaciones-ia">
            <HugeiconsIcon icon={ArrowLeft01Icon} data-icon="inline-start" aria-hidden="true" />
            Volver a aplicaciones
          </Link>
        </Button>
        <Alert variant="destructive">
          <HugeiconsIcon icon={AlertCircleIcon} aria-hidden="true" />
          <AlertTitle>No fue posible abrir la aplicación</AlertTitle>
          <AlertDescription>{error || 'La aplicación solicitada no existe.'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-7 px-5 py-8 lg:px-8 lg:py-10">
      <ApplicationHeader application={application} />
      <SummaryCards application={application} resources={resources} />

      <Tabs defaultValue="summary" className="flex flex-col gap-5">
        <div className="overflow-x-auto pb-1">
          <TabsList className="h-auto min-w-max justify-start">
            <Tab value="summary" icon={Settings01Icon}>
              Resumen
            </Tab>
            <Tab value="domains" icon={Globe02Icon}>
              Ambientes y dominios
            </Tab>
            <Tab value="databases" icon={Database01Icon}>
              Bases de datos
            </Tab>
            <Tab value="storage" icon={FolderCloudIcon}>
              S3
            </Tab>
            <Tab value="email" icon={Mail01Icon}>
              Correo SES
            </Tab>
            <Tab value="metrics" icon={ChartBarLineIcon}>
              Métricas
            </Tab>
          </TabsList>
        </div>

        <TabsContent value="summary">
          <OverviewTab application={application} />
        </TabsContent>
        <TabsContent value="domains">
          <DomainsTab application={application} />
        </TabsContent>
        <TabsContent value="databases">
          <DatabasesTab
            application={application}
            canManage={can.manageApplications}
            submitting={submitting}
            onProvision={provisionDatabase}
          />
        </TabsContent>
        <TabsContent value="storage">
          <ResourceTab
            application={application}
            resources={resources.filter((resource) => resource.resource_type === 's3_bucket')}
            type="s3_bucket"
            canManage={can.manageApplications}
            submitting={submitting}
            onRequest={requestResource}
          />
        </TabsContent>
        <TabsContent value="email">
          <EmailTab
            application={application}
            resources={resources.filter((resource) => resource.resource_type === 'ses_email')}
            canManage={can.manageApplications}
            submitting={submitting}
            onRequest={requestResource}
          />
        </TabsContent>
        <TabsContent value="metrics">
          <MetricsTab application={application} resources={resources} canView={can.observability} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ApplicationHeader({ application }) {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" className="w-fit px-0 hover:bg-transparent hover:text-primary" asChild>
        <Link to="/aplicaciones-ia">
          <HugeiconsIcon icon={ArrowLeft01Icon} data-icon="inline-start" aria-hidden="true" />
          Gestión de Aplicaciones
        </Link>
      </Button>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <HugeiconsIcon icon={CloudServerIcon} size={24} aria-hidden="true" />
          </span>
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">{application.name}</h1>
              <StatusBadge value={application.status} />
              <StatusBadge value={application.criticality} />
            </div>
            <p className="text-sm text-muted-foreground">
              {application.application_key} · {application.initiative_code || application.initiative_name}
            </p>
            <p className="max-w-4xl text-sm leading-6 text-muted-foreground">
              {application.description || 'Sin descripción registrada.'}
            </p>
          </div>
        </div>
        <EnvironmentBadges environments={application.environments} />
      </div>
    </div>
  );
}

function SummaryCards({ application, resources }) {
  const environments = application.environments || [];
  const values = [
    ['Etapa actual', application.initiative_stage, ServerStack01Icon],
    ['Evaluación', application.evaluation_status || 'Pendiente', CheckmarkCircle02Icon],
    ['Conexiones activas', environments.filter((environment) => environment.connection_available).length, Key01Icon],
    [
      'Recursos registrados',
      resources.length + environments.filter((environment) => environment.database).length,
      CloudServerIcon,
    ],
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {values.map(([label, value, icon]) => (
        <Card key={label} className="shadow-none">
          <CardContent className="flex items-center gap-4 p-5">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <HugeiconsIcon icon={icon} size={20} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-lg font-extrabold">{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function OverviewTab({ application }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.65fr)]">
      <Card className="shadow-none">
        <CardHeader>
          <CardTitle>Información de la aplicación</CardTitle>
          <CardDescription>Datos de gobierno vinculados a la iniciativa aprobada en VIA.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
          <Detail label="Iniciativa" value={application.initiative_code || application.initiative_name} />
          <Detail label="Propietario" value={application.owner_name || 'Sin owner'} />
          <Detail label="Equipo" value={application.team_name || 'Sin equipo'} />
          <Detail label="Clasificación" value={application.data_classification} />
          <Detail label="Criticidad" value={application.criticality} />
          <Detail label="Centro de costo" value={displayCostCenter(application.cost_center)} />
          <Detail label="Aprobación" value={application.approval_status} />
          <Detail label="Evaluación" value={application.evaluation_status || 'Pendiente'} />
          <Detail label="Etapa" value={application.initiative_stage} />
        </CardContent>
      </Card>
      <RepositoryCard repositoryURL={application.repository_url} />
    </div>
  );
}

function RepositoryCard({ repositoryURL }) {
  const options = useMemo(() => repositoryCloneOptions(repositoryURL), [repositoryURL]);
  return (
    <Card className="shadow-none">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <HugeiconsIcon icon={RepositoryIcon} size={18} aria-hidden="true" />
            </span>
            <div>
              <CardTitle>Repositorio</CardTitle>
              <CardDescription>{options.provider}</CardDescription>
            </div>
          </div>
          {repositoryURL && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  <HugeiconsIcon icon={CodeIcon} data-icon="inline-start" aria-hidden="true" />
                  Clonar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Copiar comando de clonación</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {options.commands.map((option) => (
                    <DropdownMenuItem
                      key={option.label}
                      onSelect={() => copyValue(option.command, `Comando ${option.label} copiado`)}>
                      <HugeiconsIcon icon={Copy01Icon} aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="font-medium">{option.label}</p>
                        <p className="truncate font-mono text-[11px] text-muted-foreground">{option.command}</p>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {repositoryURL ? (
          <>
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">Repositorio configurado</p>
              <p className="mt-1 truncate font-mono text-sm font-semibold" title={repositoryURL}>
                {options.name}
              </p>
            </div>
            <p className="text-xs leading-5 text-muted-foreground">
              Las credenciales se obtienen desde el perfil AWS o proveedor Git configurado en el equipo; VIA no guarda
              contraseñas del repositorio.
            </p>
          </>
        ) : (
          <EmptyInline icon={RepositoryIcon} text="No hay un repositorio registrado." />
        )}
      </CardContent>
    </Card>
  );
}

function DomainsTab({ application }) {
  return (
    <div className="flex flex-col gap-5">
      <Alert>
        <HugeiconsIcon icon={LockIcon} aria-hidden="true" />
        <AlertTitle>Convención de dominios administrada</AlertTitle>
        <AlertDescription>
          Los nombres se calculan desde el identificador de la aplicación y el ambiente bajo ucchristus.ai. Esto evita
          configuraciones distintas entre equipos.
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 xl:grid-cols-3">
        {(application.environments || []).map((environment) => (
          <Card key={environment.id} className="shadow-none">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle>{environment.environment.toUpperCase()}</CardTitle>
                  <CardDescription>{environment.provision_reason}</CardDescription>
                </div>
                <EnvironmentBadge
                  value={environment.environment}
                  available={environment.provision_eligible}
                  connected={environment.connection_available}
                />
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <DomainRow label="Aplicación" value={environment.application_domain} openable />
              <DomainRow label="API" value={environment.api_domain} openable />
              <DomainRow label="Base de datos" value={environment.database_domain} />
              <DomainRow label="Identidad de correo" value={environment.email_identity} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function DomainRow({ label, value, openable = false }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <HugeiconsIcon icon={Globe02Icon} size={16} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate font-mono text-xs font-medium" title={value}>
          {value}
        </p>
      </div>
      <Button
        size="icon"
        variant="ghost"
        aria-label={`Copiar dominio ${label}`}
        onClick={() => copyValue(value, 'Dominio copiado')}>
        <HugeiconsIcon icon={Copy01Icon} aria-hidden="true" />
      </Button>
      {openable && (
        <Button size="icon" variant="ghost" aria-label={`Abrir ${label}`} asChild>
          <a href={`https://${value}`} target="_blank" rel="noreferrer">
            <HugeiconsIcon icon={LinkSquare01Icon} aria-hidden="true" />
          </a>
        </Button>
      )}
    </div>
  );
}

function DatabasesTab({ application, canManage, submitting, onProvision }) {
  return (
    <div className="grid gap-4 xl:grid-cols-3">
      {(application.environments || []).map((environment) => (
        <Card key={environment.id} className="shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle>PostgreSQL {environment.environment.toUpperCase()}</CardTitle>
                <CardDescription>{environment.database_domain}</CardDescription>
              </div>
              <EnvironmentBadge
                value={environment.environment}
                available={environment.provision_eligible}
                connected={environment.connection_available}
              />
            </div>
          </CardHeader>
          <CardContent className="flex min-h-72 flex-col gap-4">
            {environment.database ? (
              <>
                <div className="flex items-center gap-3 rounded-xl border border-success/25 bg-success/5 p-4 text-success">
                  <HugeiconsIcon icon={CheckmarkCircle02Icon} size={22} aria-hidden="true" />
                  <div>
                    <p className="font-semibold">Base disponible</p>
                    <p className="text-xs opacity-80">Credenciales habilitadas según permisos.</p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                  <Detail label="Base lógica" value={environment.database.database_name} mono />
                  <Detail label="Esquema" value={environment.database.schema_name} mono />
                  <Detail label="Conexión" value={environment.use_rds_proxy ? 'RDS Proxy' : 'RDS directo'} />
                  <Detail label="Estado" value={environment.database.status} />
                </div>
              </>
            ) : (
              <>
                <EmptyInline icon={Database01Icon} text="Este ambiente todavía no tiene una base de datos." />
                <p className="text-xs leading-5 text-muted-foreground">{environment.provision_reason}</p>
                {canManage && (
                  <Button
                    className="mt-auto"
                    disabled={!environment.provision_eligible || submitting === `database-${environment.environment}`}
                    onClick={() => onProvision(environment)}>
                    <HugeiconsIcon icon={Database01Icon} data-icon="inline-start" aria-hidden="true" />
                    {submitting === `database-${environment.environment}`
                      ? 'Registrando…'
                      : `Crear base ${environment.environment.toUpperCase()}`}
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ResourceTab({ application, resources, type, canManage, submitting, onRequest }) {
  return (
    <div className="flex flex-col gap-5">
      <Alert>
        <HugeiconsIcon icon={FolderCloudIcon} aria-hidden="true" />
        <AlertTitle>Almacenamiento separado por ambiente</AlertTitle>
        <AlertDescription>
          Cada solicitud reserva un nombre único, bloqueo de acceso público, cifrado y versionado. Pendiente significa
          solicitado, no creado en AWS.
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 xl:grid-cols-3">
        {ENVIRONMENTS.map((environment) => {
          const resource = resources.find((item) => item.environment === environment);
          const eligible = environmentEligible(application.initiative_stage, environment);
          const name = `poc-ia-${application.application_key}-${environment}-pri-use1`;
          return (
            <Card key={environment} className="shadow-none">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Bucket {environment.toUpperCase()}</CardTitle>
                    <CardDescription className="font-mono">{resource?.resource_key || name}</CardDescription>
                  </div>
                  <StatusBadge value={resource?.status || (eligible ? 'Disponible para solicitar' : 'Bloqueado')} />
                </div>
              </CardHeader>
              <CardContent className="flex min-h-64 flex-col gap-4">
                <ResourceControls />
                {resource ? (
                  <ResourceRecord resource={resource} />
                ) : (
                  <>
                    <p className="text-xs leading-5 text-muted-foreground">
                      {eligible ? 'El ambiente permite registrar la solicitud.' : environmentReason(environment)}
                    </p>
                    {canManage && (
                      <Button
                        className="mt-auto"
                        disabled={!eligible || submitting === `${type}-${environment}`}
                        onClick={() => onRequest(environment, type)}>
                        <HugeiconsIcon icon={FolderCloudIcon} data-icon="inline-start" aria-hidden="true" />
                        {submitting === `${type}-${environment}`
                          ? 'Registrando…'
                          : `Solicitar bucket ${environment.toUpperCase()}`}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function ResourceControls() {
  const controls = ['Cifrado SSE-KMS', 'Acceso público bloqueado', 'Versionado habilitado'];
  return (
    <div className="flex flex-col gap-2">
      {controls.map((control) => (
        <div key={control} className="flex items-center gap-2 text-xs">
          <HugeiconsIcon icon={CheckmarkCircle02Icon} size={15} className="text-success" aria-hidden="true" />
          <span>{control}</span>
        </div>
      ))}
    </div>
  );
}

function EmailTab({ application, resources, canManage, submitting, onRequest }) {
  return (
    <div className="flex flex-col gap-5">
      <Alert variant="destructive">
        <HugeiconsIcon icon={AlertCircleIcon} aria-hidden="true" />
        <AlertTitle>SES requiere habilitación previa en poc-ia</AlertTitle>
        <AlertDescription>
          Estado AWS verificado el 3 ago 2026: la cuenta está en sandbox y no tiene identidades verificadas. VIA puede
          registrar la necesidad por ambiente, pero no mostrará el servicio como disponible hasta verificar la identidad
          y habilitar el envío de producción.
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 xl:grid-cols-3">
        {ENVIRONMENTS.map((environment) => {
          const environmentData = application.environments?.find((item) => item.environment === environment);
          const resource = resources.find((item) => item.environment === environment);
          const eligible = environmentEligible(application.initiative_stage, environment);
          return (
            <Card key={environment} className="shadow-none">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Correo {environment.toUpperCase()}</CardTitle>
                    <CardDescription>{environmentData?.email_identity}</CardDescription>
                  </div>
                  <StatusBadge value={resource?.status || (eligible ? 'Disponible para solicitar' : 'Bloqueado')} />
                </div>
              </CardHeader>
              <CardContent className="flex min-h-72 flex-col gap-4">
                <Detail label="Región" value="us-east-1" mono />
                <Detail label="Endpoint API" value="email.us-east-1.amazonaws.com" mono />
                <Detail label="Autenticación" value="Rol IAM de la aplicación" />
                {resource ? (
                  <ResourceRecord resource={resource} />
                ) : (
                  <>
                    <p className="text-xs leading-5 text-muted-foreground">
                      {eligible
                        ? 'La solicitud quedará pendiente de verificación y salida de sandbox.'
                        : environmentReason(environment)}
                    </p>
                    {canManage && (
                      <Button
                        className="mt-auto"
                        disabled={!eligible || submitting === `ses_email-${environment}`}
                        onClick={() => onRequest(environment, 'ses_email')}>
                        <HugeiconsIcon icon={Mail01Icon} data-icon="inline-start" aria-hidden="true" />
                        {submitting === `ses_email-${environment}`
                          ? 'Registrando…'
                          : `Solicitar correo ${environment.toUpperCase()}`}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function ResourceRecord({ resource }) {
  return (
    <div className="mt-auto flex flex-col gap-3 rounded-xl border bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold">Registro VIA</span>
        <StatusBadge value={resource.status} />
      </div>
      <Separator />
      <Detail label="Identificador" value={resource.resource_key} mono />
      <Detail label="Región" value={resource.region} mono />
      {resource.aws_arn && <Detail label="ARN" value={resource.aws_arn} mono />}
    </div>
  );
}

function MetricsTab({ application, resources, canView }) {
  const environments = application.environments || [];
  const totalCost = environments.reduce((total, environment) => total + (environment.estimated_monthly_cost || 0), 0);
  const [environment, setEnvironment] = useState(resolveCurrentEnvironment());
  const [service, setService] = useState('all');
  const [period, setPeriod] = useState('1h');
  const [correlationInput, setCorrelationInput] = useState('');
  const [correlationID, setCorrelationID] = useState('');
  const [observability, setObservability] = useState(null);
  const [observabilityError, setObservabilityError] = useState('');
  const [observabilityLoading, setObservabilityLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!canView) return;
    let active = true;
    const loadObservability = async () => {
      setObservabilityLoading(true);
      setObservabilityError('');
      try {
        const { data } = await api.get(`/applications/${application.id}/observability`, {
          params: {
            environment,
            service,
            period,
            ...(correlationID ? { correlation_id: correlationID } : {}),
          },
        });
        if (active) setObservability(data);
      } catch (requestError) {
        if (active) setObservabilityError(apiError(requestError));
      } finally {
        if (active) setObservabilityLoading(false);
      }
    };
    loadObservability();
    return () => {
      active = false;
    };
  }, [application.id, canView, correlationID, environment, period, refreshKey, service]);

  const applyCorrelation = () => setCorrelationID(correlationInput.trim());
  const clearCorrelation = () => {
    setCorrelationInput('');
    setCorrelationID('');
  };

  return (
    <div className="flex flex-col gap-5">
      {!canView ? (
        <Alert>
          <HugeiconsIcon icon={LockIcon} aria-hidden="true" />
          <AlertTitle>Observabilidad restringida</AlertTitle>
          <AlertDescription>
            Los registros sanitizados de CloudWatch están disponibles solamente para administradores y evaluadores
            autorizados.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <Card className="shadow-none">
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <CardTitle>Observabilidad CloudWatch</CardTitle>
                  <CardDescription>
                    Consultas acotadas y auditadas. VIA oculta credenciales, direcciones IP, correos y parámetros
                    sensibles antes de mostrar un evento.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={observabilityLoading}
                  onClick={() => setRefreshKey((value) => value + 1)}>
                  <HugeiconsIcon icon={RefreshIcon} data-icon="inline-start" aria-hidden="true" />
                  {observabilityLoading ? 'Actualizando…' : 'Actualizar'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-[180px_220px_180px_minmax(280px,1fr)_auto]">
                <FilterField label="Ambiente">
                  <Select value={environment} onValueChange={setEnvironment}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {ENVIRONMENTS.map((item) => (
                          <SelectItem key={item} value={item}>
                            {item.toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FilterField>
                <FilterField label="Servicio">
                  <Select value={service} onValueChange={setService}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="all">Todos los servicios</SelectItem>
                        <SelectItem value="api">API Lambda</SelectItem>
                        <SelectItem value="gateway">API Gateway</SelectItem>
                        <SelectItem value="provisioner">Aprovisionador</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FilterField>
                <FilterField label="Período">
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="15m">Últimos 15 minutos</SelectItem>
                        <SelectItem value="1h">Última hora</SelectItem>
                        <SelectItem value="6h">Últimas 6 horas</SelectItem>
                        <SelectItem value="24h">Últimas 24 horas</SelectItem>
                        <SelectItem value="7d">Últimos 7 días</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FilterField>
                <FilterField label="ID de correlación">
                  <Input
                    value={correlationInput}
                    onChange={(event) => setCorrelationInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') applyCorrelation();
                    }}
                    placeholder="Ej. request-123"
                    maxLength={160}
                    aria-label="ID de correlación"
                  />
                </FilterField>
                <div className="flex items-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={applyCorrelation}
                    disabled={observabilityLoading || correlationInput.trim() === correlationID}>
                    <HugeiconsIcon icon={Search01Icon} data-icon="inline-start" aria-hidden="true" />
                    Buscar
                  </Button>
                  {correlationID && (
                    <Button variant="ghost" onClick={clearCorrelation}>
                      Limpiar
                    </Button>
                  )}
                </div>
              </div>
              {correlationID && (
                <p className="text-xs text-muted-foreground">
                  Filtrando eventos por <span className="font-mono font-semibold">{correlationID}</span>. El resumen
                  conserva todas las solicitudes del período.
                </p>
              )}
            </CardContent>
          </Card>

          {observabilityError && (
            <Alert variant="destructive">
              <HugeiconsIcon icon={AlertCircleIcon} aria-hidden="true" />
              <AlertTitle>No fue posible consultar CloudWatch</AlertTitle>
              <AlertDescription>{observabilityError}</AlertDescription>
            </Alert>
          )}
          {observability?.warnings?.map((warning) => (
            <Alert key={warning}>
              <HugeiconsIcon icon={AlertCircleIcon} aria-hidden="true" />
              <AlertTitle>Resultado parcial</AlertTitle>
              <AlertDescription>{warning}</AlertDescription>
            </Alert>
          ))}

          {observabilityLoading && !observability ? (
            <ObservabilitySkeleton />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard
                  label="Solicitudes"
                  value={formatNumber(observability?.summary?.requests)}
                  icon={Activity01Icon}
                />
                <MetricCard
                  label="Respuestas 4xx"
                  value={formatNumber(observability?.summary?.http_4xx)}
                  icon={AlertCircleIcon}
                />
                <MetricCard
                  label="Respuestas 5xx"
                  value={formatNumber(observability?.summary?.http_5xx)}
                  icon={AlertCircleIcon}
                />
                <MetricCard
                  label="Latencia p95"
                  value={formatLatency(observability?.summary?.latency_p95_ms)}
                  icon={Clock01Icon}
                />
              </div>
              <CloudWatchEventsCard observability={observability} loading={observabilityLoading} />
            </>
          )}
        </>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Ambientes saludables"
          value={environments.filter((item) => item.health_status === 'Saludable').length}
          icon={CheckmarkCircle02Icon}
        />
        <MetricCard
          label="Con base activa"
          value={environments.filter((item) => item.database).length}
          icon={Database01Icon}
        />
        <MetricCard label="Recursos AWS registrados" value={resources.length} icon={CloudServerIcon} />
        <MetricCard label="Costo mensual estimado" value={formatCurrency(totalCost)} icon={DollarCircleIcon} />
      </div>
      <Card className="shadow-none">
        <CardHeader>
          <CardTitle>Estado gobernado por ambiente</CardTitle>
          <CardDescription>Despliegues, recursos y costos registrados en VIA.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ambiente</TableHead>
                <TableHead>Salud</TableHead>
                <TableHead>Versión</TableHead>
                <TableHead>Base de datos</TableHead>
                <TableHead>Recursos</TableHead>
                <TableHead className="text-right">Costo estimado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {environments.map((environment) => (
                <TableRow key={environment.id}>
                  <TableCell>
                    <EnvironmentBadge
                      value={environment.environment}
                      available={environment.provision_eligible}
                      connected={environment.connection_available}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge value={environment.health_status} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {environment.deployed_version || 'Sin despliegue'}
                  </TableCell>
                  <TableCell>{environment.database?.database_name || 'Sin aprovisionar'}</TableCell>
                  <TableCell>
                    {resources.filter((resource) => resource.environment === environment.environment).length}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(environment.estimated_monthly_cost)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function CloudWatchEventsCard({ observability, loading }) {
  const events = observability?.events || [];
  return (
    <Card className="shadow-none">
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Eventos recientes</CardTitle>
            <CardDescription>
              {events.length} eventos sanitizados · {observability?.environment?.toUpperCase() || '—'} · actualización{' '}
              {formatDateTime(observability?.generated_at)}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {observability?.console_links?.dashboard && (
              <Button size="sm" variant="outline" asChild>
                <a href={observability.console_links.dashboard} target="_blank" rel="noreferrer">
                  <HugeiconsIcon icon={ChartBarLineIcon} data-icon="inline-start" aria-hidden="true" />
                  Abrir dashboard
                </a>
              </Button>
            )}
            {observability?.console_links?.logs_insights && (
              <Button size="sm" variant="outline" asChild>
                <a href={observability.console_links.logs_insights} target="_blank" rel="noreferrer">
                  <HugeiconsIcon icon={LinkSquare01Icon} data-icon="inline-start" aria-hidden="true" />
                  Logs Insights
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
            <HugeiconsIcon icon={RefreshIcon} size={15} className="animate-spin" aria-hidden="true" />
            Actualizando eventos…
          </div>
        )}
        {!events.length ? (
          <EmptyInline icon={Activity01Icon} text="No se encontraron eventos con los filtros seleccionados." />
        ) : (
          <ScrollArea className="h-[560px] rounded-lg border">
            <div className="min-w-[1100px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">Fecha</TableHead>
                    <TableHead className="w-36">Servicio</TableHead>
                    <TableHead className="w-32">Severidad</TableHead>
                    <TableHead>Evento</TableHead>
                    <TableHead className="w-52">Correlación</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event, index) => (
                    <TableRow key={`${event.timestamp}-${index}`}>
                      <TableCell className="whitespace-nowrap text-xs">{formatDateTime(event.timestamp)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{event.service}</Badge>
                      </TableCell>
                      <TableCell>
                        <EventSeverityBadge value={event.severity} />
                      </TableCell>
                      <TableCell>
                        <p className="min-w-72 max-w-3xl break-words font-mono text-xs leading-5">{event.message}</p>
                      </TableCell>
                      <TableCell>
                        {event.correlation_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="max-w-48 justify-start font-mono"
                            title={event.correlation_id}
                            onClick={() => copyValue(event.correlation_id, 'ID de correlación copiado')}>
                            <HugeiconsIcon icon={Copy01Icon} data-icon="inline-start" aria-hidden="true" />
                            <span className="truncate">{event.correlation_id}</span>
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

function EventSeverityBadge({ value }) {
  if (value === 'Error')
    return (
      <Badge variant="outline" className="border-destructive/25 bg-destructive/10 text-destructive">
        Error
      </Badge>
    );
  if (value === 'Advertencia')
    return (
      <Badge variant="outline" className="border-warning/25 bg-warning/10 text-warning">
        Advertencia
      </Badge>
    );
  return (
    <Badge variant="outline" className="border-primary/25 bg-primary/10 text-primary">
      Info
    </Badge>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="flex min-w-0 flex-col gap-2">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ObservabilitySkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-80 rounded-xl" />
    </div>
  );
}

function MetricCard({ label, value, icon }) {
  return (
    <Card className="shadow-none">
      <CardContent className="flex items-center gap-4 p-5">
        <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <HugeiconsIcon icon={icon} size={20} aria-hidden="true" />
        </span>
        <div>
          <p className="text-xl font-extrabold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function Tab({ value, icon, children }) {
  return (
    <TabsTrigger value={value} className="gap-2">
      <HugeiconsIcon icon={icon} size={15} aria-hidden="true" />
      {children}
    </TabsTrigger>
  );
}

function Detail({ label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`${mono ? 'font-mono text-xs' : 'text-sm'} mt-1 break-words font-medium`}>{value ?? '—'}</p>
    </div>
  );
}

function EmptyInline({ icon, text }) {
  return (
    <div className="flex min-h-28 flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-5 text-center text-sm text-muted-foreground">
      <HugeiconsIcon icon={icon} size={26} aria-hidden="true" />
      <p>{text}</p>
    </div>
  );
}

function AdminLoadingState() {
  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-6 py-10">
      <Skeleton className="h-28 rounded-xl" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );
}

function environmentEligible(stage, environment) {
  return (STAGE_RANK[stage] ?? -1) >= ENVIRONMENT_RANK[environment];
}

function environmentReason(environment) {
  if (environment === 'stg') return 'STG se habilita cuando la iniciativa avanza a Piloto.';
  if (environment === 'prd') return 'PRD se habilita cuando la iniciativa avanza a Producción.';
  return 'DEV se habilita cuando la iniciativa avanza a Desarrollo.';
}

function repositoryCloneOptions(repositoryURL) {
  if (!repositoryURL) return { provider: 'Sin configurar', name: 'Sin repositorio', commands: [] };
  const codeCommit = repositoryURL.match(/^codecommit::([^:]+):\/\/(?:([^@]+)@)?(.+)$/);
  if (codeCommit) {
    const [, region, profile, repository] = codeCommit;
    return {
      provider: 'AWS CodeCommit',
      name: repository,
      commands: [
        {
          label: 'git-remote-codecommit',
          command: `git clone codecommit::${region}://${profile ? `${profile}@` : ''}${repository}`,
        },
        { label: 'HTTPS', command: `git clone https://git-codecommit.${region}.amazonaws.com/v1/repos/${repository}` },
      ],
    };
  }
  const github = repositoryURL.match(/^https?:\/\/github\.com\/([^/]+)\/([^/]+?)(?:\.git)?$/);
  if (github) {
    const [, owner, repository] = github;
    return {
      provider: 'GitHub',
      name: `${owner}/${repository}`,
      commands: [
        { label: 'HTTPS', command: `git clone https://github.com/${owner}/${repository}.git` },
        { label: 'SSH', command: `git clone git@github.com:${owner}/${repository}.git` },
        { label: 'GitHub CLI', command: `gh repo clone ${owner}/${repository}` },
      ],
    };
  }
  const name =
    repositoryURL
      .split('/')
      .filter(Boolean)
      .pop()
      ?.replace(/\.git$/, '') || repositoryURL;
  return { provider: 'Repositorio Git', name, commands: [{ label: 'Git', command: `git clone ${repositoryURL}` }] };
}

async function copyValue(value, message) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  } catch {
    toast.error('No fue posible copiar al portapapeles');
  }
}

function formatCurrency(value) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    value || 0,
  );
}

function formatNumber(value) {
  return new Intl.NumberFormat('es-CL').format(value || 0);
}

function formatLatency(value) {
  if (value === null || value === undefined) return 'Sin datos';
  return `${new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(value)} ms`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-CL', { dateStyle: 'short', timeStyle: 'medium' }).format(date);
}

function resolveCurrentEnvironment() {
  const hostname = window.location.hostname;
  if (hostname === 'via.ucchristus.ai') return 'prd';
  if (hostname === 'stg.via.ucchristus.ai') return 'stg';
  return 'dev';
}

function displayCostCenter(value) {
  if (!value || value.toLowerCase() === 'pending') return 'Por asignar';
  return value;
}
